/**
 * Aprovisionamiento de la cuenta de profesor en el primer ingreso (ADR-023).
 *
 * Compone `userService` (Firestore) y `auth.service` (Firebase Auth) sin que
 * ninguno cambie de responsabilidad. Lo llaman los tres caminos que crean la cuenta
 * de Quizoteca de alguien que ya tiene sesión de Auth: el registro con Google
 * (`useAuthStore().loginWithGoogle({ provisionIfNew: true })`), la vinculación de
 * Google a una cuenta de contraseña y el «Continuar» de «Un último paso»
 * (`useLegalConsentStore().accept`, que no puede importar el store de auth). El
 * registro con correo no pasa por aquí: el nombre lo teclea el usuario y el store
 * lo escribe en Auth antes de crear los documentos.
 *
 * El orden es el del ADR: **una** derivación → `users` → `professor_profiles` (el
 * mismo valor, espejo) → Auth **después**, y sólo si Auth venía en blanco y este
 * flujo creó `users`. Si Auth falla, Firestore se queda, se registra y el ingreso
 * sigue: el nombre de Auth se cura la próxima vez que el profesor guarde su perfil.
 * @see specs/DISENO.md
 */

import type { User } from 'firebase/auth'
import { userService } from './userService'
import { updateUserDisplayName } from './firebase/auth.service'
import { deriveDisplayName, isBlankDisplayName } from '@/utils/displayName'

export interface ProvisionResult {
  /** El nombre derivado que se escribió en los documentos que se crearon. */
  displayName: string
  /** `true` si esta llamada creó `users/{uid}`. */
  createdUserDocument: boolean
  /** `true` si se escribió el nombre en Firebase Auth y la escritura resolvió. */
  syncedAuthDisplayName: boolean
}

export async function provisionProfessorAccount(user: User): Promise<ProvisionResult> {
  const displayName = deriveDisplayName(user.displayName, user.email)

  const createdUserDocument = await userService.ensureUserDocument(user, 'professor', displayName)
  await userService.ensureProfessorProfile(user, displayName)

  let syncedAuthDisplayName = false
  if (createdUserDocument && isBlankDisplayName(user.displayName)) {
    try {
      await updateUserDisplayName(user, displayName)
      syncedAuthDisplayName = true
    } catch (err) {
      console.error(
        '[accountProvisioning] el nombre quedó en Firestore pero no se pudo copiar a Firebase Auth',
        err
      )
    }
  }

  return { displayName, createdUserDocument, syncedAuthDisplayName }
}
